"use client";

import { PictureAsset } from "@/components/common/picture-asset";
import { ResourceContext } from "@/contexts/resource-context";
import dayjs from "dayjs";
import "dayjs/locale/pl";
import { model } from "./model";
import { type FieldValues, useForm } from "react-hook-form";
import { vestResolver } from "@hookform/resolvers/vest";
import { fromFormDefinitionToValidationSchema } from "@/forms/validation/validation-factory";
import { useImmer } from "use-immer";
import { clientPost } from "@/service/client-api/client-api";

import { preSubmit } from "@/forms/validation";
import { logEvent } from "@/service/logs/log";
import { getAssetImage } from "@/service/image/asset";
import { TextField } from "@/forms/fields/text-field/text-field";
dayjs.locale("pl"); // set the locale to Polish
import { Form } from "@/components/ui/form";
import { LoaderIcon, LockKeyholeOpenIcon, XIcon } from "lucide-react";
import { FormError } from "@/forms/form-error";
import { Button } from "@/components/ui/button";
import { ReactNode, useContext, useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { FieldsContainer } from "@/forms/fields-container";

export const ExclusiveAccess = ({
  exclusiveAccess,
  children,
  link,
}: {
  exclusiveAccess: ExclusiveAccessVM;
  children: ReactNode;
  link: string;
}) => {
  const [fields, setFields] = useImmer(model);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const { logView } = useContext(ResourceContext);
  const { publicAccessSince, resourcePatron, patronEmail, title } =
    exclusiveAccess;
  const form = useForm({
    resolver: vestResolver(fromFormDefinitionToValidationSchema(fields)),
  });
  const { handleSubmit } = form;
  const [isOpenModal, setIsOpenModal] = useState(false);

  useEffect(() => {
    if (isOpenModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isOpenModal]);

  async function onSubmit({ code }: FieldValues) {
    if (preSubmit(loading, setLoading, setError, setFields) === "BREAK") {
      return;
    }

    try {
      await clientPost(
        "exclusive_access.get_access",
        {},
        {
          code,
        },
      );
      logEvent("exclusive_access", logView, {
        resource_title: title,
        code,
      });
      location.href = link;
      setIsOpenModal(false);
    } catch (e) {
      setError((e as ServerError)?.error ?? "Podany kod  jest błędny.");
    }

    setLoading(false);
  }

  const representative = patronEmail ? `(${patronEmail})` : "";

  return (
    <Form {...form}>
      <div className={"flex flex-col items-center"}>
        <div className="w-full" onClick={() => setIsOpenModal(true)}>
          {children}
        </div>
        <Dialog open={isOpenModal} onOpenChange={() => setIsOpenModal(false)}>
          <DialogContent
            className="w-4/5 max-w-[400px]"
            innerClassName={"p-6 pt-12"}
          >
            <div className="h-28 rounded-xl bg-violet-200/30 py-[19px]">
              <PictureAsset
                ext={"png"}
                alt="Kłódka"
                className="h-full w-full object-contain object-center"
                source={getAssetImage("/exclusive-access/password")}
              />
            </div>
            <div className={"flex w-full flex-col items-center gap-2 pt-6"}>
              <div className="text-lg font-semibold">
                Przedpremierowy dostęp do kursu
              </div>
              <div className="text-muted-foreground text-center text-sm">
                Do {dayjs(publicAccessSince).format("D MMMM YYYY [roku]")}{" "}
                możliwy jest jedynie przedpremierowy dostęp do kursu. Dostęp ten
                możesz uzyskać kontaktując się z przedstawicielem firmy{" "}
                {resourcePatron.name} {representative}, który udostępni Ci
                dedykowany kod autoryzujący.
              </div>
              <form onSubmit={handleSubmit(onSubmit)} className="w-full px-0.5">
                <FieldsContainer>
                  <TextField field={fields.code} />
                </FieldsContainer>
                <div className={""}>
                  <Button variant={"blue"} type="submit" className="w-full">
                    <LockKeyholeOpenIcon className="h-4 w-4" />
                    {loading && (
                      <LoaderIcon className={"animate-spin"} size={16} />
                    )}
                    Odblokuj kurs
                  </Button>
                </div>
                <FormError error={error} />
              </form>
            </div>
            <div
              className="absolute top-4 right-4 cursor-pointer"
              onClick={() => setIsOpenModal(false)}
            >
              <XIcon className="h-4 w-4" />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Form>
  );
};
